<script setup lang="ts">
import { searchUserAttention, UserAttentionType } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '@/components/design/composite/GaiaFilterValuePanel.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import {
  ATTENTION_TYPE_LABEL,
  SIGNING_ACTION_VERB,
  attentionStatus,
  attentionStatusLabel,
  attentionTarget,
  attentionTitle,
  attentionUpdatedAt,
  isSigningAttention,
  type UserAttentionItem,
} from '@/components/design/domain/attention/attention';
import { IconChat, IconSign } from '@/components/design/icons';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useUserAttentionStore } from '@/stores/userAttentionStore';
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { NFlex, NIcon, type DataTableColumns } from 'naive-ui';
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { useRouter } from 'vue-router';

dayjs.extend(relativeTime);

/**
 * Everything waiting on the user, across every organisation they are in —
 * Home's "Action required" preview, in full (Insights Home mock, "List —
 * Action required"). It opens on the active organisation like the preview;
 * the organisation chip widens it. Opening an item from another organisation
 * means switching into it first, so that row asks before it goes. "Sign/Approve now"
 * never asks: the signing link is fetched for the user, not the organisation.
 */

const router = useRouter();
const toast = useGaiaToast();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const userAttentionStore = useUserAttentionStore();

const items = ref<UserAttentionItem[]>([]);
const totalCount = ref(0);
const loading = ref(false);
const typeCounts = ref<Record<UserAttentionType, number>>({
  [UserAttentionType.CHAT_THREAD]: 0,
  [UserAttentionType.SIGNING]: 0,
});

const TYPE_TABS = ['all', UserAttentionType.SIGNING, UserAttentionType.CHAT_THREAD];

const { currentPage, pageSize, activeTab, columnFilters } = useListQuerySync({
  defaultTab: 'all',
  allowedTabs: TYPE_TABS,
  tabParam: 'type',
});

const paginationConfig = usePaginationConfig(currentPage, pageSize, totalCount, {
  // The endpoint pages at most 50.
  pageSizes: [10, 25, 50],
});

// ── Organisation filter ─────────────────────────────────────────────────────
//
// `?f_org=` holds the picked organisations. Absent means the default (the
// active organisation); `any` is the deliberate "every organisation", so it
// survives a reload rather than snapping back to the default.

const ANY_ORG = 'any';

const activeOrganisationUid = computed(
  () => authStore.backendUser?.active_organisation?.uid ?? null
);

const organisations = computed(() => authStore.organisations);

function organisationName(uid: string): string {
  return organisations.value.find(org => org.uid === uid)?.name ?? 'Unknown organisation';
}

const defaultOrganisationUids = computed(() =>
  activeOrganisationUid.value ? [activeOrganisationUid.value] : []
);

/** Picked organisation uids; empty = every organisation. */
const selectedOrganisationUids = computed<string[]>(() => {
  if (!('org' in columnFilters.value)) return defaultOrganisationUids.value;
  const picked = columnFilters.value.org;
  return picked.includes(ANY_ORG) ? [] : picked;
});

function setOrganisationUids(uids: string[]): void {
  columnFilters.value = { ...columnFilters.value, org: uids.length ? uids : [ANY_ORG] };
}

const orgPanelOpen = ref(false);
const orgSearch = ref('');

const orgFilterValues = computed<GaiaFilterValue[]>(() => {
  const term = orgSearch.value.trim().toLowerCase();
  return organisations.value
    .filter(org => !term || org.name.toLowerCase().includes(term))
    .map(org => ({
      key: org.uid,
      label: org.uid === activeOrganisationUid.value ? `${org.name} (active)` : org.name,
      checked: selectedOrganisationUids.value.includes(org.uid),
    }));
});

function toggleOrganisation(uid: string): void {
  const picked = selectedOrganisationUids.value;
  setOrganisationUids(
    picked.includes(uid) ? picked.filter(value => value !== uid) : [...picked, uid]
  );
}

const orgChipValues = computed(() => selectedOrganisationUids.value.map(organisationName));

// ── Type tabs ───────────────────────────────────────────────────────────────

const selectedTypes = computed<UserAttentionType[] | null>(() =>
  // 'all' is no type, so it filters nothing.
  isEnumValue(UserAttentionType, activeTab.value) ? [activeTab.value] : null
);

const typeTabs = computed<GaiaSegmentedOption[]>(() => {
  const chat = typeCounts.value[UserAttentionType.CHAT_THREAD];
  const signing = typeCounts.value[UserAttentionType.SIGNING];
  return [
    { label: 'All', value: 'all', count: chat + signing },
    {
      label: ATTENTION_TYPE_LABEL[UserAttentionType.SIGNING],
      value: UserAttentionType.SIGNING,
      count: signing,
    },
    {
      label: ATTENTION_TYPE_LABEL[UserAttentionType.CHAT_THREAD],
      value: UserAttentionType.CHAT_THREAD,
      count: chat,
    },
  ];
});

const isDefaultFilter = computed(
  () => !('org' in columnFilters.value) && activeTab.value === 'all'
);

function resetFilters(): void {
  activeTab.value = 'all';
  columnFilters.value = {};
}

// ── Fetching ────────────────────────────────────────────────────────────────

function organisationQuery(): string[] | null {
  return selectedOrganisationUids.value.length ? [...selectedOrganisationUids.value] : null;
}

/** The tab counts: one `limit: 0` search per type, under the organisation filter. */
async function fetchTypeCount(type: UserAttentionType): Promise<number> {
  const response = await searchUserAttention({
    body: { organisation_uids: organisationQuery(), types: [type], limit: 0 },
    throwOnError: true,
  });
  return response.data.result_count;
}

async function fetchItems(): Promise<void> {
  loading.value = true;
  try {
    const [list, chat, signing] = await Promise.all([
      searchUserAttention({
        body: {
          organisation_uids: organisationQuery(),
          types: selectedTypes.value,
          limit: pageSize.value,
          offset: (currentPage.value - 1) * pageSize.value,
        },
        throwOnError: true,
      }),
      fetchTypeCount(UserAttentionType.CHAT_THREAD),
      fetchTypeCount(UserAttentionType.SIGNING),
    ]);
    items.value = list.data.results;
    totalCount.value = list.data.result_count;
    typeCounts.value = {
      [UserAttentionType.CHAT_THREAD]: chat,
      [UserAttentionType.SIGNING]: signing,
    };
  } catch (error) {
    logger.error('Error fetching user attention:', error);
    toast.error('Failed to load what needs your attention.');
  } finally {
    loading.value = false;
  }
}

watch([currentPage, pageSize], () => {
  if (currentPage.value > 0 && pageSize.value > 0) {
    void fetchItems();
  }
});

watch([activeTab, columnFilters], () => {
  currentPage.value = 1;
  void fetchItems();
});

// The store refetches on every live attention event; following it keeps this
// list current without a second socket handler.
watch(
  () => userAttentionStore.items,
  () => void fetchItems()
);

// ── Opening a row ───────────────────────────────────────────────────────────

/** The row whose "switch organisation?" bubble is open. */
const pendingSwitchUid = ref<string | null>(null);
const switching = ref(false);

function isForeign(row: UserAttentionItem): boolean {
  return row.organisation !== null && row.organisation.uid !== activeOrganisationUid.value;
}

function openItem(row: UserAttentionItem): void {
  const target = attentionTarget(row);
  if (!target) return;
  if (isForeign(row)) {
    pendingSwitchUid.value = row.uid;
    return;
  }
  void router.push(target);
}

// Same landing as the sidebar's switcher — a switch re-scopes every request,
// so a full document load drops the old organisation's cached state — only it
// lands on the item rather than on home.
async function switchAndOpen(row: UserAttentionItem): Promise<void> {
  const target = attentionTarget(row);
  if (!target || !row.organisation || switching.value) return;
  switching.value = true;
  const switched = await organizationStore.switchCurrentUserOrganization(row.organisation.uid);
  if (!switched) {
    switching.value = false;
    toast.error(organizationStore.error ?? 'Failed to switch organisation');
    return;
  }
  window.location.assign(router.resolve(target).href);
}

/** Clicks on a control inside the row are the control's, not the row's. */
function handleRowClick(row: UserAttentionItem, event: MouseEvent): void {
  const element = event.target as HTMLElement;
  if (element.closest('button') || element.closest('a')) return;
  openItem(row);
}

// ── Table ───────────────────────────────────────────────────────────────────

function renderItem(row: UserAttentionItem): VNode {
  const cell = h(NFlex, { vertical: true, size: 2, wrap: false, style: { minWidth: 0 } }, () => [
    h(
      GaiaText,
      { size: 'item', weight: 'semibold', truncate: true, title: attentionTitle(row) },
      () => attentionTitle(row)
    ),
    isSigningAttention(row) && row.signing_request.expiration_date
      ? h(
          GaiaText,
          { size: 'micro', tone: 'faint', truncate: true },
          () => `Expires ${dayjs(row.signing_request.expiration_date).format('D MMM YYYY')}`
        )
      : null,
  ]);

  if (!isForeign(row) || !row.organisation) return cell;

  const organisation = row.organisation.name;
  // The bubble hangs off the item cell; the row click opens it.
  return h(
    GaiaPopconfirm,
    {
      tone: 'neutral',
      placement: 'bottom-start',
      width: 300,
      title: `Switch to ${organisation}?`,
      confirmLabel: 'Switch',
      show: pendingSwitchUid.value === row.uid,
      'onUpdate:show': (show: boolean) => {
        pendingSwitchUid.value = show ? row.uid : null;
      },
      onConfirm: () => void switchAndOpen(row),
    },
    {
      trigger: () => cell,
      default: () => `This item lives in ${organisation}. Switch organisation to open it.`,
    }
  );
}

function renderAction(row: UserAttentionItem): VNode | null {
  if (isSigningAttention(row)) {
    return h(
      GaiaButton,
      {
        type: 'primary',
        size: 'small',
        'aria-label': `${SIGNING_ACTION_VERB[row.reason]} ${attentionTitle(row)} (opens in a new tab)`,
        onClick: () => void userAttentionStore.openSigningLink(row),
      },
      () => `${SIGNING_ACTION_VERB[row.reason]} now ↗`
    );
  }
  if (!attentionTarget(row)) return null;
  return h(
    GaiaButton,
    {
      size: 'small',
      loading: switching.value && pendingSwitchUid.value === row.uid,
      onClick: () => {
        openItem(row);
      },
    },
    () => 'Open thread'
  );
}

const columns = computed((): DataTableColumns<UserAttentionItem> => [
  {
    title: 'Item',
    key: 'item',
    minWidth: 260,
    render: renderItem,
  },
  {
    title: 'Type',
    key: 'type',
    width: 120,
    render: row =>
      h(
        GaiaTag,
        { variant: 'muted', size: 'small' },
        {
          icon: () => h(NIcon, { component: isSigningAttention(row) ? IconSign : IconChat }),
          default: () => ATTENTION_TYPE_LABEL[row.type],
        }
      ),
  },
  {
    title: 'Status',
    key: 'status',
    width: 240,
    render: row =>
      h(GaiaStatusIndicator, { status: attentionStatus(row) }, () => attentionStatusLabel(row)),
  },
  {
    title: 'Organisation',
    key: 'organisation',
    width: 200,
    render: row =>
      h(
        GaiaText,
        { size: 'control', tone: 'tertiary', truncate: true },
        () => row.organisation?.name ?? '—'
      ),
  },
  {
    title: 'Updated',
    key: 'updated_at',
    width: 140,
    render: row =>
      h(GaiaText, { size: 'control', tone: 'tertiary' }, () =>
        dayjs(attentionUpdatedAt(row)).fromNow()
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 140,
    align: 'right',
    render: renderAction,
  },
]);

onMounted(() => {
  void fetchItems();
});
</script>

<template>
  <layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'home' }">{{ navLabels.home }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.actionRequired }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="20" :wrap="false">
      <n-flex vertical :size="6" :wrap="false">
        <n-flex align="baseline" :size="10" :wrap="false">
          <gaia-header :level="1">{{ navLabels.actionRequired }}</gaia-header>
          <gaia-count-label>
            {{ totalCount === 1 ? '1 item' : `${totalCount} items` }}
          </gaia-count-label>
        </n-flex>
        <gaia-text size="body" tone="subtle">
          Everything waiting on you: chat threads that need an answer and agreements to sign.
        </gaia-text>
      </n-flex>

      <n-flex align="center" :size="8">
        <gaia-segmented
          :value="activeTab"
          :options="typeTabs"
          size="medium"
          @update:value="(value: string) => (activeTab = value)"
        />

        <gaia-popover v-model:show="orgPanelOpen" placement="bottom-start">
          <template #trigger>
            <gaia-filter-chip
              field="Organisation"
              :values="orgChipValues.slice(0, 2)"
              :overflow="orgChipValues.length > 2 ? `+${orgChipValues.length - 2}` : undefined"
              :active="orgPanelOpen"
              @remove="setOrganisationUids([])"
              @remove-value="
                (name: string) =>
                  setOrganisationUids(
                    selectedOrganisationUids.filter(uid => organisationName(uid) !== name)
                  )
              "
            />
          </template>
          <gaia-filter-value-panel
            v-model:search="orgSearch"
            :values="orgFilterValues"
            search-placeholder="Search organisations"
            :picked-label="
              selectedOrganisationUids.length
                ? `${selectedOrganisationUids.length} selected`
                : 'All organisations'
            "
            @toggle="toggleOrganisation"
            @clear="setOrganisationUids([])"
          />
        </gaia-popover>

        <gaia-button v-if="!isDefaultFilter" text size="small" @click="resetFilters">
          Reset
        </gaia-button>
      </n-flex>

      <gaia-table
        table-layout="fixed"
        :columns="columns"
        :data="items"
        :loading="loading"
        :remote="true"
        :pagination="paginationConfig"
        :row-key="(row: UserAttentionItem) => row.uid"
        :row-props="
          (row: UserAttentionItem) => ({
            onClick: (event: MouseEvent) => handleRowClick(row, event),
            style: { cursor: attentionTarget(row) ? 'pointer' : 'default' },
          })
        "
      >
        <template #empty>
          <gaia-empty-state title="Nothing needs your attention">
            {{ isDefaultFilter ? 'You are all caught up.' : 'Nothing matches these filters.' }}
            <template v-if="!isDefaultFilter" #actions>
              <gaia-button @click="resetFilters">Reset filters</gaia-button>
            </template>
          </gaia-empty-state>
        </template>
      </gaia-table>
    </n-flex>
  </layout>
</template>
